import React, { FunctionComponent } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tippy'

import { ClinTheme } from '../../../ClinTheme'
import { ClinButton } from '../../../components/ClinButton'
import { ClinGroup } from '../../../components/ClinGroup'
import { ClinIcon } from '../../../components/ClinIcon'
import { ClinIconPathName } from '../../../components/ClinIcon/ClinIcon.paths'
import { ClinText } from '../../../components/ClinText'
import { TypographyVariant } from '../../../components/ClinText/ClinText.styles'
import { useAppContext } from '../../../context/app'
import { AnalyticsEvent } from '../../../services/Analytics'
import analyticsServiceSingleton from '../../../services/Analytics/initAnalytics'
import { StyledDetailsContact } from './ProductDetail.styles'

interface IProductDetailContactProps {
  itemSku: string
}

export const ProductDetailContact: FunctionComponent<
  IProductDetailContactProps
> = ({ itemSku }) => {
  const { t } = useTranslation()
  const { supportContact } = useAppContext()

  const [hoveredLink, setHoveredLink] = React.useState<boolean>(false)
  const [iconName, setIconName] = React.useState<ClinIconPathName>(
    ClinIconPathName.CopyLink
  )
  const [open, setOpen] = React.useState<boolean>(false)
  const [tooltipText, setTooltipText] = React.useState<string>(
    t('product_detail:product_detail_contact.hover_link_text')
  )

  const copyLinkHandler = () => {
    const url = window.location.href
    navigator.clipboard.writeText(url)
  }
  const handleMouseLeave = () => {
    setIconName(ClinIconPathName.CopyLink)
    setTooltipText(t('product_detail:product_detail_contact.hover_link_text'))
    setOpen(false)
    setHoveredLink(false)
  }

  const handleMouseClick = () => {
    copyLinkHandler()
    setIconName(ClinIconPathName.Success)
    setTooltipText(t('product_detail:product_detail_contact.hovered_link_text'))
    analyticsServiceSingleton.trackEvent(AnalyticsEvent.TrackCopyLink, {
      sku: itemSku
    })
  }
  return (
    <div>
      <ClinText
        variant={TypographyVariant.Paragraph}
        fontWeight={ClinTheme.fontWeights.medium}
        marginBottom={ClinTheme.space[3]}
        marginTop={'0px'}
      >
        {t('product_detail:product_detail_contact.title')}
      </ClinText>

      <div style={{ marginBottom: '16px' }}>
        <ClinGroup alignItems="center" justifyContent="flex-start">
          <ClinIcon
            iconName={ClinIconPathName.Phone}
            style={{ marginRight: 0 }}
            iconFill={ClinTheme.colors.primary}
          />
          <a
            className="display-telephone"
            href={`tel: ${supportContact.rawPhoneNumber}`}
            style={{ margin: 0 }}
            onClick={() => {
              analyticsServiceSingleton.trackEvent(
                AnalyticsEvent.ContactClicked,
                {
                  sku: itemSku,
                  contactType: 'telephone'
                }
              )
            }}
          >
            <ClinText
              className={'telephone'}
              as="span"
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.primary}
              marginLeft={ClinTheme.space[2]}
            >
              {supportContact?.csPhoneNumber}
            </ClinText>
          </a>
        </ClinGroup>
      </div>
      <div
        style={{
          marginBottom: '8px'
        }}
      >
        <ClinGroup alignItems="center" justifyContent="flex-start">
          <ClinIcon
            iconName={ClinIconPathName.Mail}
            style={{ marginRight: 0 }}
            iconFill={ClinTheme.colors.primary}
          />
          <a
            className="raw-email"
            href={`mailto:${supportContact?.csEmailAddress}`}
            style={{ width: '100%' }}
            onClick={() => {
              analyticsServiceSingleton.trackEvent(
                AnalyticsEvent.ContactClicked,
                { sku: itemSku, contactType: 'email' }
              )
            }}
          >
            <ClinText
              className="email"
              as="span"
              fontWeight={ClinTheme.fontWeights.medium}
              color={ClinTheme.colors.primaryLight}
              wordBreak="break-word"
            >
              {supportContact?.csEmailAddress}
            </ClinText>
          </a>
        </ClinGroup>
      </div>
      <StyledDetailsContact
        onMouseEnter={() => {
          setHoveredLink(true)
          setOpen(true)
        }}
        onMouseLeave={() => {
          handleMouseLeave()
        }}
        onClick={() => {
          handleMouseClick()
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleMouseClick()
          }
        }}
      >
        <ClinGroup alignItems="center" justifyContent="flex-start">
          <Tooltip
            theme="light"
            position="top"
            trigger="manual"
            open={open}
            arrow={true}
            title={tooltipText}
            style={{
              marginLeft: '0',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ClinIcon
              iconName={ClinIconPathName.CopyLinkArrow}
              style={{ marginRight: 0, paddingLeft: `${ClinTheme.space[2]}px` }}
              iconFill={ClinTheme.colors.primary}
              iconHeight={`${ClinTheme.space[4]}px`}
              iconSize={`${ClinTheme.space[4]}px`}
              viewBox="0,0,26,27"
            />

            <ClinButton
              variant="linkButton"
              color={ClinTheme.colors.primaryLight}
              style={{ margin: 0, height: `${ClinTheme.space[6]}px` }}
            >
              <ClinText
                as="span"
                color={'inherit'}
                fontWeight={ClinTheme.fontWeights.medium}
              >
                {t('product_detail:product_detail_contact.link')}
              </ClinText>
            </ClinButton>
            {hoveredLink && (
              <ClinIcon
                iconName={iconName}
                style={{
                  marginRight: 0,
                  marginLeft: `${ClinTheme.space[1]}px`
                }}
                iconFill={
                  iconName === ClinIconPathName.Success
                    ? '#00A223'
                    : `${ClinTheme.colors.primary}`
                }
                iconHeight={20}
                iconSize={20}
                viewBox="0 0 20 13"
              />
            )}
          </Tooltip>
        </ClinGroup>
      </StyledDetailsContact>
    </div>
  )
}
